import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Gamepad2, Home, Briefcase, Cpu } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { categoriasApi } from '@/lib/api';

const iconMap: Record<string, React.ElementType> = {
  'acessorios': Gamepad2,
  'decoracao': Home,
  'utilitarios': Briefcase,
  'prototipagem': Cpu,
};

const gradientMap: Record<string, string> = {
  'acessorios': 'from-orange-500 to-red-600',
  'decoracao': 'from-cyan-500 to-blue-600',
  'utilitarios': 'from-green-500 to-emerald-600',
  'prototipagem': 'from-yellow-500 to-orange-500',
};

export function CategoriesSection() {
  const { data: categorias, isLoading } = useQuery({
    queryKey: ['categorias-publico'],
    queryFn: async () => {
      const response = await categoriasApi.listarPublico();
      const data = response.data;
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        return data.data as any[];
      }
      return (Array.isArray(data) ? data : []) as any[];
    },
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1] as const,
      },
    },
  };

  if (isLoading) {
    return (
      <section className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-64 bg-gray-900/50 rounded-3xl animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-gray-950 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-orange-500/10 to-cyan-500/10 border border-orange-500/20 text-orange-400 text-sm font-medium mb-4"
          >
            Navegue por Categoria
          </motion.span>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Encontre o que você{' '}
            <span className="bg-gradient-to-r from-orange-400 to-cyan-400 bg-clip-text text-transparent">
              precisa
            </span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Explore nossas categorias e descubra produtos incríveis impressos em 3D 
            com a mais alta qualidade
          </p>
        </motion.div>

        {/* Categories Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {categorias?.map((categoria: any) => {
            const Icon = iconMap[categoria.slug] || Briefcase;
            const gradient = gradientMap[categoria.slug] || 'from-gray-500 to-gray-600';
            
            return (
              <motion.div key={categoria.id} variants={itemVariants}>
                <Link to={`/catalogo?categoria=${categoria.id}`}>
                  <motion.div
                    whileHover={{ y: -8, scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                    className="group relative h-64 rounded-3xl overflow-hidden cursor-pointer"
                  >
                    {/* Background Gradient */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-90 group-hover:opacity-100 transition-opacity`} />
                    
                    {/* Pattern Overlay */}
                    <div className="absolute inset-0 opacity-20"
                      style={{
                        backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                        backgroundSize: '20px 20px',
                      }}
                    />
                    
                    {/* Content */}
                    <div className="relative h-full p-6 flex flex-col justify-between">
                      {/* Icon */}
                      <motion.div
                        whileHover={{ rotate: 10, scale: 1.1 }}
                        transition={{ duration: 0.3 }}
                        className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center"
                      >
                        <Icon className="w-7 h-7 text-white" />
                      </motion.div>

                      {/* Text */}
                      <div>
                        <h3 className="text-xl font-bold text-white mb-1">
                          {categoria.nome}
                        </h3>
                        <p className="text-white/70 text-sm line-clamp-2 mb-3">
                          {categoria.descricao || 'Produtos selecionados com qualidade premium'}
                        </p>
                        <motion.div
                          initial={{ x: 0 }}
                          whileHover={{ x: 5 }}
                          className="flex items-center gap-2 text-white font-medium text-sm"
                        >
                          Explorar 
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </motion.div>
                      </div>
                    </div>

                    {/* Decorative Circle */}
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0.3 }}
                      whileHover={{ scale: 1.2, opacity: 0.5 }}
                      transition={{ duration: 0.5 }}
                      className="absolute -bottom-8 -right-8 w-32 h-32 rounded-full bg-white/20"
                    />
                  </motion.div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="text-center mt-12"
        >
          <Link
            to="/catalogo"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/20 text-white font-medium hover:bg-white hover:text-gray-900 transition-all duration-300"
          >
            Ver Todos os Produtos
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
